import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Send, Paperclip } from 'lucide-react';

interface InputAreaProps {
  /** Function to call when message is sent */
  onSendMessage: (message: string) => void;
  /** Function to call when file is attached */
  onAttachment?: (files: File[]) => void;
  /** Placeholder text for input */
  placeholder?: string;
  /** Whether the input is disabled */
  disabled?: boolean;
  /** Whether message sending is in progress */
  isLoading?: boolean;
  /** Error message to display */
  errorMessage?: string | null;
  /** Text to display when disabled */
  disabledText?: string;
  /** Whether file attachments are allowed */
  allowAttachments?: boolean;
}

interface InputAreaRef {
  setValue: (value: string) => void;
  clear: () => void;
  focus: () => void;
}

/**
 * InputArea component for the chat interface
 * Handles user message input, sending messages, and attachments
 * Compliant with V2 Technical Specification
 */
const InputArea = forwardRef<InputAreaRef, InputAreaProps>(({
  onSendMessage,
  onAttachment,
  placeholder = 'Customized ChatBots!',
  disabled = false,
  isLoading = false,
  errorMessage = null,
  disabledText = 'Chat is currently unavailable',
  allowAttachments = false,
}, ref) => {
  const [message, setMessage] = useState<string>('');
  const inputRef = useRef<HTMLInputElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Expose methods to parent via ref
  useImperativeHandle(ref, () => ({
    setValue: (value: string) => {
      setMessage(value);
      if (inputRef.current) {
        inputRef.current.focus();
      }
    },
    clear: () => {
      setMessage('');
    },
    focus: () => {
      if (inputRef.current) {
        inputRef.current.focus();
      }
    }
  }), []);

  // Focus input on component mount only - avoid re-focusing during typing
  useEffect(() => {
    if (inputRef.current && !disabled && !isLoading) {
      inputRef.current.focus();
    }
  }, []); // Remove dependencies to prevent re-focus during typing

  // Handle message change
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setMessage(e.target.value);
  };

  // Handle sending message
  const handleSendMessage = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !disabled && !isLoading) {
      onSendMessage(trimmedMessage);
      setMessage('');
    }
  };

  // Handle keydown events (Enter to send)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  // Handle file attachment
  const handleAttachmentClick = () => {
    if (fileInputRef.current && !disabled && !isLoading) {
      fileInputRef.current.click();
    }
  };

  // Process file selection
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files.length > 0 && onAttachment) {
      onAttachment(Array.from(e.target.files));
      e.target.value = ''; // Clear input for future selections
    }
  };

  return (
    <div className="h-16 bg-white border-t border-border-gray px-4 py-3 relative flex flex-col" data-testid="input-area">
      {errorMessage && (
        <div className="text-red-500 text-sm mb-2 px-2 py-1 bg-red-50 rounded-md w-full">
          {errorMessage}
        </div>
      )}
      
      <div className="flex items-center w-full">
        {allowAttachments && (
          <>
            <button
              type="button"
              className="w-10 h-10 rounded-full bg-transparent text-gray-500 border border-border-gray mr-2 cursor-pointer flex items-center justify-center hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed min-w-44 min-h-44"
              onClick={handleAttachmentClick}
              disabled={disabled || isLoading}
              aria-label="Attach file"
              data-testid="attachment-button"
            >
              <Paperclip size={20} />
            </button>
            <input
              type="file"
              ref={fileInputRef}
              className="hidden"
              onChange={handleFileChange}
              disabled={disabled || isLoading}
              data-testid="file-input"
            />
          </>
        )}
        
        <input
          type="text"
          ref={inputRef}
          className="flex-1 h-10 px-4 py-2 border border-border-gray rounded-3xl text-base text-gray-900 bg-white outline-none focus:border-primary disabled:bg-gray-50 disabled:opacity-80 disabled:cursor-not-allowed mobile:text-sm"
          placeholder={placeholder}
          value={message}
          onChange={handleMessageChange}
          onKeyDown={handleKeyDown}
          disabled={disabled || isLoading}
          data-testid="text-input"
        />
        
        <button
          type="button"
          className="w-10 h-10 rounded-full bg-primary text-white border-none ml-2 cursor-pointer flex items-center justify-center hover:brightness-110 disabled:bg-border-gray disabled:cursor-not-allowed disabled:text-gray-400 min-w-44 min-h-44"
          onClick={handleSendMessage}
          disabled={disabled || isLoading || !message.trim()}
          aria-label="Send message"
          data-testid="send-button"
        >
          <Send size={20} />
        </button>
      </div>
      
      {disabled && disabledText && (
        <div className="text-gray-500 text-xs text-center mt-1">
          {disabledText}
        </div>
      )}
    </div>
  );
});

InputArea.displayName = 'InputArea';

export default InputArea; 